import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Download, X, CreditCard, Mail, Building2, Phone, Shield } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import DOTCompanyFetchModal from "./dot-company-fetch-modal";
import { insertCompanySchema } from "@shared/schema";

const formSchema = insertCompanySchema.extend({
  name: z.string().min(1, "Company name is required"),
  email: z.string().email("Valid email is required").optional().or(z.literal("")),
  dotNumber: z.string().regex(/^\d*$/, "DOT number must be numeric only").optional().or(z.literal("")),
  dbaName: z.string().optional().or(z.literal("")),
});

type FormData = z.infer<typeof formSchema>;

interface OfficerToCreate {
  firstName: string;
  lastName: string;
  email: string | null;
}

interface EditCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData, officersToCreate?: OfficerToCreate[]) => void;
  isLoading: boolean;
  company: any;
}

export default function EditCompanyModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  company,
}: EditCompanyModalProps) {
  const [showDOTFetchModal, setShowDOTFetchModal] = useState(false);
  const [officersToCreate, setOfficersToCreate] = useState<OfficerToCreate[]>([]);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      dbaName: "",
      email: "",
      phone: "",
      address: "",
      type: "service_bureau",
      ein: "",
      status: "inquiry",
      dotNumber: "",
      inactiveDotNumbers: "",
      dotPin: "",
      loginGovUsername: "",
      loginGovAuthMethod: undefined,
      billingPreference: "pay_at_service",
      batchInterval: null,
      billingEmail: "",
      billingEmailSameAsCompany: true,
    },
  });

  useEffect(() => {
    if (company) {
      form.reset({
        name: company.name || "",
        dbaName: company.dotProfile?.dbaName || "",
        email: company.email || "",
        phone: company.phone || "",
        address: company.address || "",
        type: company.type || "service_bureau",
        ein: company.ein || "",
        status: company.status || "inquiry",
        dotNumber: company.dotProfile?.dotNumber || "",
        inactiveDotNumbers: company.inactiveDotNumbers || "",
        dotPin: company.dotPin || "",
        loginGovUsername: company.loginGovUsername || "",
        loginGovAuthMethod: company.loginGovAuthMethod || undefined,
        billingPreference: company.billingPreference || "pay_at_service",
        batchInterval: company.batchInterval || null,
        billingEmail: company.billingEmail || "",
        billingEmailSameAsCompany: company.billingEmailSameAsCompany ?? true,
      });
      setOfficersToCreate([]);
    }
  }, [company, form]);

  const billingPreference = form.watch("billingPreference");
  const billingEmailSameAsCompany = form.watch("billingEmailSameAsCompany");

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data, officersToCreate.length > 0 ? officersToCreate : undefined);
  };

  const handleDOTDataApply = (data: {
    companyFields: Partial<{ name: string; dbaName: string; phone: string; email: string; address: string; dotNumber: string }>;
    officersToCreate: OfficerToCreate[];
  }) => {
    if (data.companyFields.name) form.setValue("name", data.companyFields.name);
    if (data.companyFields.dbaName) form.setValue("dbaName", data.companyFields.dbaName);
    if (data.companyFields.phone) form.setValue("phone", data.companyFields.phone);
    if (data.companyFields.email) form.setValue("email", data.companyFields.email);
    if (data.companyFields.address) form.setValue("address", data.companyFields.address);
    if (data.companyFields.dotNumber) form.setValue("dotNumber", data.companyFields.dotNumber);
    
    setOfficersToCreate(data.officersToCreate);
  };

  const dotNumber = form.watch("dotNumber");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Company</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-5 overflow-y-auto flex-1 pr-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Building2 className="h-4 w-4" />
              <span>Company Information</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dbaName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DBA Name (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Doing Business As" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="service_bureau">Service Bureau</SelectItem>
                        <SelectItem value="direct_client">Direct Client</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ein"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>EIN (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company EIN" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="inquiry">Inquiry</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="dotNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DOT Number (Optional)</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input placeholder="Enter DOT number" {...field} className="flex-1" />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowDOTFetchModal(true)}
                        data-testid="button-open-dot-fetch-edit"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Fetch
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="inactiveDotNumbers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Inactive DOT Numbers (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter old/inactive DOT numbers" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {officersToCreate.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-blue-800">Officers to create as contacts:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  {officersToCreate.map((officer, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span>{officer.firstName} {officer.lastName}</span>
                      {officer.email && (
                        <span className="text-blue-500 text-xs">({officer.email})</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setOfficersToCreate(prev => prev.filter((_, i) => i !== index))}
                        className="text-blue-400 hover:text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Separator />

            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Phone className="h-4 w-4" />
              <span>Contact Information</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="company@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="(555) 123-4567" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Company address" {...field} value={field.value ?? ""} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Shield className="h-4 w-4" />
              <span>Government Credentials</span>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="dotPin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DOT PIN (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter DOT PIN" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="loginGovUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Login.gov Username (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Login.gov username" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="loginGovAuthMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Login.gov Auth Method</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="face_touch_unlock">Face or touch unlock</SelectItem>
                        <SelectItem value="authentication_application">Authentication app</SelectItem>
                        <SelectItem value="security_key">Security key</SelectItem>
                        <SelectItem value="text_call">Text/call</SelectItem>
                        <SelectItem value="backup_code">Backup code</SelectItem>
                        <SelectItem value="piv_cac">PIV or CAC</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />
            
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <CreditCard className="h-4 w-4" />
              <span>Billing Settings</span>
            </div>
            
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="billingEmailSameAsCompany"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value ?? true}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (checked) {
                            form.setValue("billingEmail", "");
                          }
                        }}
                        data-testid="checkbox-billing-email-same"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="cursor-pointer">
                        Use company email for billing
                      </FormLabel>
                      <FormDescription>
                        {billingEmailSameAsCompany 
                          ? `Invoices will be sent to: ${form.watch("email") || "(no company email set)"}`
                          : "Specify a different billing email below"}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              {!billingEmailSameAsCompany && (
                <FormField
                  control={form.control}
                  name="billingEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing Email</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Input 
                            type="email" 
                            placeholder="billing@company.com" 
                            {...field}
                            value={field.value ?? ""}
                            data-testid="input-billing-email"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="billingPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Preference</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (value === "pay_at_service") {
                          form.setValue("batchInterval", null);
                        }
                      }} 
                      value={field.value || "pay_at_service"}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-billing-preference">
                          <SelectValue placeholder="Select billing preference" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pay_at_service">Pay at Service</SelectItem>
                        <SelectItem value="batch">Batch Billing</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {field.value === "batch" 
                        ? "Invoices will be grouped and sent on a schedule" 
                        : "Invoice sent immediately after each service"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {billingPreference === "batch" && (
                <FormField
                  control={form.control}
                  name="batchInterval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Batch Interval</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-batch-interval">
                            <SelectValue placeholder="Select interval" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Biweekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How often to send batch invoices
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            {company?.stripeCustomerId && (
              <div className="rounded-md bg-muted p-3">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Stripe Customer ID:</span>{" "}
                  <code className="text-xs">{company.stripeCustomerId}</code>
                </p>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4 border-t mt-4 sticky bottom-0 bg-background pb-1">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {showDOTFetchModal && (
        <DOTCompanyFetchModal
          open={showDOTFetchModal}
          onClose={() => setShowDOTFetchModal(false)}
          dotNumber={dotNumber || ""}
          currentData={{
            name: company?.name,
            dbaName: company?.dotProfile?.dbaName,
            phone: company?.phone,
            email: company?.email,
            address: company?.address,
          }}
          mode="edit"
          onApply={handleDOTDataApply}
        />
      )}
    </Dialog>
  );
}
